import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styles from '../CitationSourcesForm.module.scss';
import { FormValues } from '../CitationSourcesForm';
import {
  Button,
  FormControl,
  Icon,
  IconList,
  Input,
  ComboBox,
} from 'components/core';
import { CitationImportSourceFormData } from 'types';

type Props = {
  sourceOptions: string[];
  usedSourceOptions: CitationImportSourceFormData[];
  rowIndex: number;
  removeRow: () => void;
};

export const CitationSourceFormRow = ({
  sourceOptions,
  usedSourceOptions,
  rowIndex,
  removeRow,
}: Props) => {
  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = useFormContext<FormValues>();

  const usedSources = usedSourceOptions.map((source) => source.sourceName);

  // Filter options based on whats used
  const filteredSourceOptions = useMemo(() => {
    return sourceOptions.filter((source) => !usedSources.includes(source));
  }, [sourceOptions, usedSources]);

  const validate = (value: number) => {
    if (!/^[0-9]+$/.test(value.toString())) {
      return 'Please enter a positive number';
    }
    return undefined;
  };

  return (
    <>
      <div className={styles.CitationSourcesForm_formCol}>
        <Controller
          control={control}
          name={`citationImportSources.${rowIndex}.sourceName` as const}
          rules={{ required: 'Please select a source' }}
          render={({ field }) => {
            const error =
              errors.citationImportSources?.[rowIndex]?.sourceName?.message;
            return (
              <FormControl error={error}>
                <ComboBox
                  {...field}
                  placeholder="Select source..."
                  data={filteredSourceOptions}
                  onChange={(value) => field.onChange(value)}
                  aria-label="Import Citation Source Select"
                  required
                  className={[
                    styles.CitationSourcesForm_formSelect,
                    error
                      ? styles.CitationSourcesForm_formSelectError
                      : undefined,
                  ].join(' ')}
                />
              </FormControl>
            );
          }}
        />
      </div>

      <div className={styles.CitationSourcesForm_formCol}>
        <FormControl
          error={
            errors.citationImportSources?.[rowIndex]?.numberOfCitations?.message
          }
        >
          <Input
            className={styles.CitationSourcesForm_formInput}
            size="sm"
            type="neutral"
            inputType="number"
            min={0}
            aria-label="Number of references"
            isError={!!errors.citationImportSources?.root?.message}
            required
            {...register(
              `citationImportSources.${rowIndex}.numberOfCitations` as const,
              {
                valueAsNumber: true,
                required: 'Please enter a count',
                onChange: () => {
                  // Manually retrigger the top level validation onChange
                  // May not be needed in future if we use a resolver validation schema
                  // Solution to use trigger found here https://github.com/react-hook-form/react-hook-form/issues/2369
                  trigger('citationImportSources');
                },
                validate: { validate },
              }
            )}
          />
        </FormControl>
      </div>

      <div className={styles.CitationSourcesForm_removeButton}>
        <Button
          iconOnly
          htmlType="button"
          variant="ghost"
          aria-label="Remove citation source"
          onClick={removeRow}
        >
          <Icon icon={IconList.light.faTrashCan} />
        </Button>
      </div>
    </>
  );
};
